import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {
  TileGroup, ListItem, Button
} from '@one-thd/sui-atomic-components';
import { ArrowDown, ArrowUp } from '@one-thd/sui-icons';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { StandardRefinement } from '../Refinements/StandardRefinement';
import { NumericRangeRefinement } from '../Refinements/CustomNumericRange/NumericRangeRefinement';
import { sortRefinements, isRefinementActive, extractNavParamFromClearAllURL } from '../../product-results-helpers';
import { ProductResultsContext } from '../ProductResultsContext';
import { getInitialItemsToShowCount } from '../../utils/showMoreLessUtility';

const NumericRangeDimension = ({
  dimension,
  onChange,
  onDisplayData,
  enableMultiStore,
  onMultiStoreSelectionChange,
  multiStoreSelection,
  fitCompatibility,
  metadata,
  selectedRefinements
}) => {

  const { data } = useContext(ProductResultsContext);
  const navParam = extractNavParamFromClearAllURL(data);

  const [selectedRefinementData, setSelectedRefinementData] = useState({
    label: null,
    refinementKey: null
  });

  const [showMore, setShowMore] = useState(false);

  const {
    refinements = []
  } = dimension || {};

  const getRefinementId = (refinement, refinementIndex) => {
    const { refinementKey } = refinement;
    return `${refinementKey}-${refinementIndex}`;
  };

  const handleOnChange = ({
    refinement,
    dimension: dimensionCustomRefinement,
    refinementRange
  }) => {
    if (onChange) {
      if (refinementRange) {
        onChange({
          multiRefinements: refinementRange,
          dimension: { ...dimension, dimensionId: 'custom_refinement' }
        });
      } else {
        onChange({
          refinement,
          dimension: dimensionCustomRefinement
        });
      }
    }
    return null;
  };

  const handleRefinementTileClick = (_event, selectedRefinement) => {
    if (!selectedRefinement) {
      return;
    }
    setSelectedRefinementData({
      label: selectedRefinement.label,
      refinementKey: selectedRefinement.refinementKey
    });

    if (typeof onChange === 'function') {
      onChange({ refinement: selectedRefinement, dimension });
    }
  };

  const nestedRefinements = refinements || [];
  const initialItemsToShowCount = getInitialItemsToShowCount(dimension, nestedRefinements);

  const refinementsToShow = showMore ? nestedRefinements.length : initialItemsToShowCount;
  const showMoreItemCount = nestedRefinements.length - initialItemsToShowCount;
  const sortedRefinements = [...nestedRefinements].sort(sortRefinements);
  const visibleRefinements = sortedRefinements.slice(0, refinementsToShow);

  const showListItemClassNames = classNames(
    'sui-flex',
    'sui-justify-center',
    'sui-items-center',
    'sui-relative',
    'sui-underline',
    'sui-w-full',
    'sui-box-border',
    'sui-text-center',
    'sui-font-regular',
    'sui-text-base',
    'sui-normal-case',
    'sui-pb-1',
    'sui-pt-2'
  );

  const showButtonClassNames = classNames(
    'sui-inline-flex',
    'sui-items-center',
    'sui-cursor-pointer',
    'sui-p-0',
    'sui-bg-transparent',
    'sui-border-0',
    'sui-w-auto'
  );

  const showArrowIconClassNames = classNames(
    'sui-w-4',
    'sui-h-4',
    'sui-flex-shrink-0'
  );

  const handleSeeMore = (event) => {
    event.preventDefault();
    setShowMore(!showMore);
  };

  return (
    <>
      {fitCompatibility && (
        <div
          role="button"
          tabIndex={0}
          aria-label="fit-compatibility"
          className="sui-mb-3"
        >
          <FitCompatibility
            type="MINI"
            drawerPosition="left"
            imageCacheEnabled
            navParam={navParam}
            data={data}
          />
        </div>
      )}
      <NumericRangeRefinement
        dimension={dimension}
        metadata={metadata}
        onChange={handleOnChange}
      />
      <TileGroup
        value={selectedRefinementData.label}
        aria-label={`${dimension?.label} Options`}
        orientation="vertical"
        onChange={handleRefinementTileClick}
        grow
        key={selectedRefinementData.refinementKey}
        data-testid="numeric-refinement-secondary-drawer"
        data-instoredisplay="true"
      >
        {
          visibleRefinements?.map((refinement, refinementIndex) => {
            const id = getRefinementId(refinement, refinementIndex);
            const isRefinementSelected = isRefinementActive(refinement.refinementKey, selectedRefinements);

            return (
              <StandardRefinement
                dimension={dimension}
                id={id}
                key={id}
                onChange={handleOnChange}
                enableMultiStore={enableMultiStore}
                refinement={refinement}
                onMultiStoreSelectionChange={onMultiStoreSelectionChange}
                multiStoreSelection={multiStoreSelection}
                onDisplay={onDisplayData.onDisplay}
                isRefinementSelected={isRefinementSelected}
              />
            );
          })
        }
      </TileGroup>
      {nestedRefinements.length > initialItemsToShowCount && (
              <ListItem className={showListItemClassNames}>
                <Button
                  variant="text"
                  onClick={handleSeeMore}
                  aria-label={showMore
                    ? 'Show fewer refinements'
                    : `Show ${showMoreItemCount} more refinements`}
                  className={showButtonClassNames}
                >
                  <span>
                    {showMore ? 'Show Less' : `Show ${showMoreItemCount} More`}
                  </span>
                  <span className="sui-ml-2 sui-mt-1">
                    {showMore ? <ArrowUp className={showArrowIconClassNames} />
                      : <ArrowDown className={showArrowIconClassNames} />}
                  </span>
                </Button>
              </ListItem>
            )}
    </>
  );
};

NumericRangeDimension.propTypes = {
  dimension: PropTypes.shape({
    dimensionId: PropTypes.string,
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(
      PropTypes.shape({})
    )
  }).isRequired,
  fitCompatibility: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onMultiStoreSelectionChange: PropTypes.func,
  enableMultiStore: PropTypes.bool,
  multiStoreSelection: PropTypes.string,
  onDisplayData: PropTypes.shape({
    onDisplay: PropTypes.string,
    onDisplayChange: PropTypes.func,
    isStoreDisplay: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.bool
    ]),
    itemCount: PropTypes.string
  }),
  metadata: PropTypes.shape({
    canonicalUrl: PropTypes.string
  }).isRequired,
  selectedRefinements: PropTypes.arrayOf(PropTypes.shape({}))
};

NumericRangeDimension.defaultProps = {
  onDisplayData: {
    onDisplay: false,
    onDisplayChange: null,
    isStoreDisplay: 'false',
    itemCount: null
  },
  fitCompatibility: false,
  enableMultiStore: false,
  onMultiStoreSelectionChange: null,
  multiStoreSelection: null,
  selectedRefinements: null
};

NumericRangeDimension.displayName = 'NumericRangeDimension';

export { NumericRangeDimension };
